import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NbIconModule, NbTooltipModule } from '@nebular/theme';
import {
  GradientStop,
  GradientType,
  Hsv,
  formatHex,
  hexToRgb,
  hsvToRgb,
  parseColorValue,
  parseHex,
  rgbToHex,
  rgbToHsv,
  serializeColorValue,
} from './color-value';

/** Ein Verlaufsstopp im Editor — mit fester Id für `track` (die Reihenfolge
 *  ändert sich beim Ziehen). */
interface EditorStop extends GradientStop {
  id: number;
}

type Mode = 'solid' | 'gradient';

const GRADIENT_TYPES: GradientType[] = ['linear', 'radial', 'conic'];

/**
 * Inhalt des Farbauswahl-Popovers von `app-color-input` (Figma-artig):
 * Reiter „Einfarbig“ / „Verlauf“, Sättigungs-/Helligkeitsfläche, Farbton- und
 * Deckkraftregler, Pipette (nur wenn der Browser `EyeDropper` kennt), Hex und
 * R/G/B. Im Verlauf: Art (Linear / Kreisförmig / Winkel), Leiste mit ziehbaren
 * Stopps (Klick auf die Leiste fügt einen hinzu), Stoppliste, Winkel,
 * Umkehren. Der Wert ist immer der String aus `color-value.ts`.
 *
 * Läuft als ComponentPortal im CDK-Overlay (Container in `nb-layout`, CLAUDE.md
 * Regel 12) — `app-color-input` öffnet, positioniert und schließt ihn.
 */
@Component({
  selector: 'app-color-picker-panel',
  standalone: true,
  imports: [CommonModule, TranslateModule, NbIconModule, NbTooltipModule],
  templateUrl: './color-picker-panel.component.html',
  styleUrl: './color-picker-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPickerPanelComponent implements OnChanges {
  @Input() value = '#000000';
  @Input() allowGradient = false;
  @Output() valueChange = new EventEmitter<string>();
  @Output() closeRequest = new EventEmitter<void>();

  readonly gradientTypes = GRADIENT_TYPES;
  readonly eyeDropperSupported =
    typeof window !== 'undefined' && 'EyeDropper' in window;

  mode: Mode = 'solid';
  /** Einfarbig. */
  solidHex = '#000000';
  solidAlpha = 1;
  /** Verlauf. */
  gradientType: GradientType = 'linear';
  angle = 90;
  stops: EditorStop[] = [];
  selectedStopId = 0;

  /** HSV der gerade bearbeiteten Farbe — gemerkt, damit der Farbton bei
   *  Grau/Schwarz nicht auf 0 springt. */
  hsv: Hsv = { h: 0, s: 0, v: 0 };

  /** Vorberechnete Anzeige-Werte (einmal je Änderung, nicht je CD-Lauf). */
  view = {
    hueColor: '#ff0000',
    editHex: '#000000',
    editRgb: { r: 0, g: 0, b: 0 },
    editAlphaPct: 100,
    editCss: '#000000',
    alphaTrack: '',
    gradientCss: '',
    barCss: '',
  };

  private nextId = 1;
  private lastEmitted?: string;
  private hasGradientState = false;
  private drag: null | {
    kind: 'sv' | 'hue' | 'alpha' | 'stop';
    el: HTMLElement;
    stopId?: number;
  } = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && this.value !== this.lastEmitted) {
      this.load(this.value);
    }
    if (changes['allowGradient'] && !this.allowGradient && this.mode === 'gradient') {
      this.mode = 'solid';
    }
    this.refreshView();
  }

  // ------------------------------------------------------------- Zustand

  private load(value: string) {
    const parsed = parseColorValue(value);
    if (parsed?.kind === 'gradient' && this.allowGradient) {
      this.mode = 'gradient';
      this.gradientType = parsed.type;
      this.angle = parsed.angle;
      this.stops = parsed.stops.map((s) => ({ ...s, id: this.nextId++ }));
      this.selectedStopId = this.stops[0].id;
      this.hasGradientState = true;
      this.solidHex = parsed.stops[0].hex;
      this.solidAlpha = parsed.stops[0].alpha;
    } else {
      this.mode = 'solid';
      if (parsed?.kind === 'solid') {
        this.solidHex = parsed.hex;
        this.solidAlpha = parsed.alpha;
      } else if (parsed?.kind === 'gradient') {
        this.solidHex = parsed.stops[0].hex;
        this.solidAlpha = parsed.stops[0].alpha;
      }
    }
    this.syncHsvFromEdit();
  }

  get selectedStop(): EditorStop | undefined {
    return this.stops.find((s) => s.id === this.selectedStopId);
  }

  private get editHex(): string {
    return this.mode === 'gradient' ? this.selectedStop?.hex ?? '#000000' : this.solidHex;
  }

  private get editAlpha(): number {
    return this.mode === 'gradient' ? this.selectedStop?.alpha ?? 1 : this.solidAlpha;
  }

  private setEdit(hex: string, alpha: number, syncHsv = true) {
    if (this.mode === 'gradient') {
      const stop = this.selectedStop;
      if (!stop) return;
      stop.hex = hex;
      stop.alpha = alpha;
    } else {
      this.solidHex = hex;
      this.solidAlpha = alpha;
    }
    if (syncHsv) this.syncHsvFromEdit();
    this.commit();
  }

  private syncHsvFromEdit() {
    const next = rgbToHsv(hexToRgb(this.editHex));
    // Grau/Schwarz haben keinen Farbton — den bisherigen stehen lassen.
    if (next.s === 0 || next.v === 0) next.h = this.hsv.h;
    if (next.v === 0) next.s = this.hsv.s;
    this.hsv = next;
  }

  private serialize(): string {
    if (this.mode === 'gradient') {
      return serializeColorValue({
        kind: 'gradient',
        type: this.gradientType,
        angle: this.angle,
        stops: this.stops,
      });
    }
    return serializeColorValue({ kind: 'solid', hex: this.solidHex, alpha: this.solidAlpha });
  }

  private commit() {
    const value = this.serialize();
    this.refreshView();
    if (value === this.lastEmitted) return;
    this.lastEmitted = value;
    this.value = value;
    this.valueChange.emit(value);
  }

  private refreshView() {
    const hex = this.editHex;
    const alpha = this.editAlpha;
    const hue = hsvToRgb({ h: this.hsv.h, s: 1, v: 1 });
    const sorted = [...this.stops].sort((a, b) => a.position - b.position);
    const stopList = sorted.map((s) => `${formatHex(s.hex, s.alpha)} ${Math.round(s.position)}%`).join(', ');
    this.view = {
      hueColor: rgbToHex(hue),
      editHex: hex,
      editRgb: hexToRgb(hex),
      editAlphaPct: Math.round(alpha * 100),
      editCss: formatHex(hex, alpha),
      alphaTrack: `linear-gradient(to right, ${formatHex(hex, 0)}, ${hex})`,
      gradientCss:
        this.mode === 'gradient' && sorted.length >= 2
          ? serializeColorValue({ kind: 'gradient', type: this.gradientType, angle: this.angle, stops: sorted })
          : '',
      barCss: sorted.length >= 2 ? `linear-gradient(90deg, ${stopList})` : '',
    };
    this.cdr.markForCheck();
  }

  // --------------------------------------------------------------- Reiter

  setMode(mode: Mode) {
    if (mode === this.mode || (mode === 'gradient' && !this.allowGradient)) return;
    if (mode === 'gradient') {
      if (!this.hasGradientState) {
        // Startverlauf aus der aktuellen Farbe: links die Farbe, rechts
        // dieselbe Farbe heller — sofort sichtbar, ohne Transparenz.
        const base = rgbToHsv(hexToRgb(this.solidHex));
        const light = rgbToHex(hsvToRgb({ h: base.h, s: base.s * 0.45, v: Math.min(1, base.v * 0.6 + 0.4) }));
        this.gradientType = 'linear';
        this.angle = 90;
        this.stops = [
          { id: this.nextId++, hex: this.solidHex, alpha: this.solidAlpha, position: 0 },
          { id: this.nextId++, hex: light, alpha: this.solidAlpha, position: 100 },
        ];
        this.selectedStopId = this.stops[0].id;
        this.hasGradientState = true;
      }
      this.mode = 'gradient';
    } else {
      const first = [...this.stops].sort((a, b) => a.position - b.position)[0];
      if (first) {
        this.solidHex = first.hex;
        this.solidAlpha = first.alpha;
      }
      this.mode = 'solid';
    }
    this.syncHsvFromEdit();
    this.commit();
  }

  onTabKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.setMode(this.mode === 'solid' ? 'gradient' : 'solid');
      this.focusSelector(`[role=tab][aria-selected=true]`);
    }
  }

  // ------------------------------------------------------- Verlauf: Art/Winkel

  setType(type: GradientType) {
    if (type === this.gradientType) return;
    this.gradientType = type;
    this.commit();
  }

  onAngleInput(event: Event) {
    const n = parseFloat((event.target as HTMLInputElement).value);
    if (!Number.isFinite(n)) return;
    this.angle = ((Math.round(n) % 360) + 360) % 360;
    this.commit();
  }

  reverse() {
    this.stops = this.stops.map((s) => ({ ...s, position: 100 - s.position }));
    this.commit();
  }

  // ------------------------------------------------------------- Stopps

  selectStop(id: number) {
    if (this.selectedStopId === id) return;
    this.selectedStopId = id;
    this.syncHsvFromEdit();
    this.refreshView();
  }

  addStop(position?: number) {
    const sorted = [...this.stops].sort((a, b) => a.position - b.position);
    let pos = position;
    if (pos === undefined) {
      // Ohne Klickposition: in die größte Lücke.
      let best = { gap: -1, at: 50 };
      for (let i = 0; i < sorted.length - 1; i++) {
        const gap = sorted[i + 1].position - sorted[i].position;
        if (gap > best.gap) best = { gap, at: sorted[i].position + gap / 2 };
      }
      pos = best.at;
    }
    pos = Math.round(Math.min(100, Math.max(0, pos)));
    const color = this.colorAt(sorted, pos);
    const stop: EditorStop = { id: this.nextId++, hex: color.hex, alpha: color.alpha, position: pos };
    this.stops = [...this.stops, stop];
    this.selectedStopId = stop.id;
    this.syncHsvFromEdit();
    this.commit();
  }

  removeStop(id: number) {
    if (this.stops.length <= 2) return;
    this.stops = this.stops.filter((s) => s.id !== id);
    if (this.selectedStopId === id) this.selectedStopId = this.stops[0].id;
    this.syncHsvFromEdit();
    this.commit();
  }

  onStopPositionInput(stop: EditorStop, event: Event) {
    const n = parseFloat((event.target as HTMLInputElement).value);
    if (!Number.isFinite(n)) return;
    stop.position = Math.round(Math.min(100, Math.max(0, n)));
    this.commit();
  }

  onStopHexInput(stop: EditorStop, event: Event) {
    const parsed = parseHex((event.target as HTMLInputElement).value);
    if (!parsed || parsed.hex.length !== 7) return;
    stop.hex = parsed.hex;
    if (stop.id === this.selectedStopId) this.syncHsvFromEdit();
    this.commit();
  }

  onStopOpacityInput(stop: EditorStop, event: Event) {
    const n = parseFloat((event.target as HTMLInputElement).value);
    if (!Number.isFinite(n)) return;
    stop.alpha = Math.min(100, Math.max(0, n)) / 100;
    this.commit();
  }

  onBarClick(event: MouseEvent) {
    // Klicks auf einen Griff wählen nur aus (siehe onStopPointerDown).
    if ((event.target as HTMLElement).closest('.cp-stop-handle')) return;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    if (!rect.width) return;
    this.addStop(((event.clientX - rect.left) / rect.width) * 100);
  }

  onStopKeydown(stop: EditorStop, event: KeyboardEvent) {
    const step = event.shiftKey ? 10 : 1;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      stop.position = Math.max(0, stop.position - step);
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      stop.position = Math.min(100, stop.position + step);
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      this.removeStop(stop.id);
      return;
    } else {
      return;
    }
    event.preventDefault();
    this.selectStop(stop.id);
    this.commit();
  }

  trackStop(_: number, stop: EditorStop) {
    return stop.id;
  }

  stopLabelIndex(stop: EditorStop): number {
    return this.stops.indexOf(stop) + 1;
  }

  private colorAt(sorted: GradientStop[], pos: number): { hex: string; alpha: number } {
    if (!sorted.length) return { hex: '#000000', alpha: 1 };
    if (pos <= sorted[0].position) return sorted[0];
    const last = sorted[sorted.length - 1];
    if (pos >= last.position) return last;
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i];
      const b = sorted[i + 1];
      if (pos >= a.position && pos <= b.position) {
        const t = b.position === a.position ? 0 : (pos - a.position) / (b.position - a.position);
        const ca = hexToRgb(a.hex);
        const cb = hexToRgb(b.hex);
        return {
          hex: rgbToHex({
            r: ca.r + (cb.r - ca.r) * t,
            g: ca.g + (cb.g - ca.g) * t,
            b: ca.b + (cb.b - ca.b) * t,
          }),
          alpha: Math.round((a.alpha + (b.alpha - a.alpha) * t) * 100) / 100,
        };
      }
    }
    return last;
  }

  // ------------------------------------------------- Einfarb-Editor: Eingaben

  onHexInput(event: Event) {
    const parsed = parseHex((event.target as HTMLInputElement).value);
    if (!parsed) return;
    // 8-stellig bringt die Deckkraft mit, 6-stellig lässt sie stehen.
    const raw = (event.target as HTMLInputElement).value.replace('#', '').trim();
    const alpha = raw.length === 8 || raw.length === 4 ? parsed.alpha : this.editAlpha;
    this.setEdit(parsed.hex, alpha);
  }

  onRgbInput(channel: 'r' | 'g' | 'b', event: Event) {
    const n = parseInt((event.target as HTMLInputElement).value, 10);
    if (!Number.isFinite(n)) return;
    const rgb = { ...hexToRgb(this.editHex), [channel]: Math.min(255, Math.max(0, n)) };
    this.setEdit(rgbToHex(rgb), this.editAlpha);
  }

  onAlphaInput(event: Event) {
    const n = parseFloat((event.target as HTMLInputElement).value);
    if (!Number.isFinite(n)) return;
    this.setEdit(this.editHex, Math.min(100, Math.max(0, n)) / 100, false);
  }

  async pickFromScreen() {
    const Ctor = (window as any).EyeDropper;
    if (!Ctor) return;
    try {
      const result = await new Ctor().open();
      const parsed = parseHex(result?.sRGBHex ?? '');
      if (parsed) this.setEdit(parsed.hex, this.editAlpha);
    } catch {
      // Abgebrochen (Escape) — nichts zu tun.
    }
  }

  // ------------------------------------------------- Flächen und Regler (Zeiger)

  onPointerDown(kind: 'sv' | 'hue' | 'alpha', event: PointerEvent) {
    if (event.button !== 0) return;
    const el = event.currentTarget as HTMLElement;
    event.preventDefault();
    el.focus();
    el.setPointerCapture?.(event.pointerId);
    this.drag = { kind, el };
    this.applyPointer(event);
  }

  onStopPointerDown(stop: EditorStop, event: PointerEvent) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget as HTMLElement;
    handle.focus();
    const bar = handle.parentElement as HTMLElement;
    handle.setPointerCapture?.(event.pointerId);
    this.drag = { kind: 'stop', el: bar, stopId: stop.id };
    this.selectStop(stop.id);
  }

  onPointerMove(event: PointerEvent) {
    if (!this.drag) return;
    this.applyPointer(event);
  }

  onPointerUp() {
    this.drag = null;
  }

  private applyPointer(event: PointerEvent) {
    const drag = this.drag;
    if (!drag) return;
    const rect = drag.el.getBoundingClientRect();
    const x = rect.width ? Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)) : 0;
    const y = rect.height ? Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)) : 0;
    switch (drag.kind) {
      case 'sv':
        this.setHsv({ ...this.hsv, s: x, v: 1 - y });
        break;
      case 'hue':
        this.setHsv({ ...this.hsv, h: x * 360 });
        break;
      case 'alpha':
        this.setEdit(this.editHex, Math.round(x * 100) / 100, false);
        break;
      case 'stop': {
        const stop = this.stops.find((s) => s.id === drag.stopId);
        if (!stop) return;
        stop.position = Math.round(x * 100);
        this.commit();
        break;
      }
    }
  }

  private setHsv(hsv: Hsv) {
    this.hsv = { h: Math.min(360, Math.max(0, hsv.h)), s: Math.min(1, Math.max(0, hsv.s)), v: Math.min(1, Math.max(0, hsv.v)) };
    this.setEdit(rgbToHex(hsvToRgb(this.hsv)), this.editAlpha, false);
  }

  // ------------------------------------------------- Flächen und Regler (Tasten)

  onSvKeydown(event: KeyboardEvent) {
    const step = event.shiftKey ? 0.1 : 0.01;
    const { s, v } = this.hsv;
    const map: Record<string, Partial<Hsv>> = {
      ArrowLeft: { s: s - step },
      ArrowRight: { s: s + step },
      ArrowUp: { v: v + step },
      ArrowDown: { v: v - step },
    };
    const change = map[event.key];
    if (!change) return;
    event.preventDefault();
    this.setHsv({ ...this.hsv, ...change });
  }

  onHueKeydown(event: KeyboardEvent) {
    const delta = this.sliderDelta(event, 1, 10, 360);
    if (delta === null) return;
    event.preventDefault();
    const h = typeof delta === 'object' ? delta.to : this.hsv.h + delta;
    this.setHsv({ ...this.hsv, h });
  }

  onAlphaKeydown(event: KeyboardEvent) {
    const delta = this.sliderDelta(event, 1, 10, 100);
    if (delta === null) return;
    event.preventDefault();
    const pct = typeof delta === 'object' ? delta.to : this.view.editAlphaPct + delta;
    this.setEdit(this.editHex, Math.min(100, Math.max(0, pct)) / 100, false);
  }

  /** Pfeile ±step (Shift ±big), Home/End → {to}. */
  private sliderDelta(event: KeyboardEvent, step: number, big: number, max: number): number | { to: number } | null {
    const s = event.shiftKey ? big : step;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        return -s;
      case 'ArrowRight':
      case 'ArrowUp':
        return s;
      case 'PageDown':
        return -big;
      case 'PageUp':
        return big;
      case 'Home':
        return { to: 0 };
      case 'End':
        return { to: max };
      default:
        return null;
    }
  }

  // ---------------------------------------------------------------- Fokus

  /** Erstes bedienbares Element — `app-color-input` fokussiert es beim Öffnen. */
  focusFirst() {
    this.focusSelector('[role=tab][aria-selected=true], .cp-sv');
  }

  private focusSelector(selector: string) {
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(selector)?.focus());
  }
}
